import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";
import { error } from "../../../../../actions/snackbarActions";
import { errorMessages } from "../../../../../Helpers/ErrorMessages";

/* ─── Constants ─── */
const WHO_STAGES = ["I", "II", "III", "IV"];
const ART_REGIMENS = [
    "TDF + 3TC + DTG (preferred 1st line)",
    "TDF + 3TC + EFV",
    "AZT + 3TC + DTG",
    "AZT + 3TC + ATV/r",
    "TDF + 3TC + ATV/r",
    "ABC + 3TC + DTG",
    "Other",
];
const FEEDING_OPTIONS = [
    { value: "exclusive_breastfeeding", label: "Exclusive Breastfeeding" },
    { value: "replacement_feeding",     label: "Replacement Feeding" },
    { value: "mixed",                   label: "Mixed Feeding (discouraged)" },
];
const PARTNER_RESULT_OPTIONS = ["reactive", "non_reactive"];

/**
 * Clinical decision fields only.
 * Lab test results are read from the lab module via /obgyn/emtct/lab-results
 * and are never stored in this record.
 */
const INITIAL_FORM = {
    /* Lab results (entered by clinician) */
    hiv_test_result:             "",      // reactive | non_reactive
    hiv_test_date:               "",
    cd4_count:                   "",
    cd4_date:                    "",
    viral_load_count:            "",
    viral_load_date:             "",
    syphilis_result:             "",      // reactive | non_reactive
    syphilis_date:               "",
    hepatitis_b_result:          "",      // positive | negative
    hepatitis_b_date:            "",
    /* ART management */
    woman_already_on_art:        false,
    woman_art_start_date:        "",
    woman_art_regimen:           "",
    woman_art_number:            "",
    woman_who_stage:             "",
    /* Partner */
    partner_tested:              false,
    partner_test_date:           "",
    partner_test_result:         "",
    partner_on_art:              false,
    stk_given_to_partner:        false,
    partner_disclosure_done:     false,
    /* STI treatment decisions */
    syphilis_woman_treated:      false,
    syphilis_partner_treated:    false,
    /* Infant plan */
    infant_arv_prophylaxis_plan: "",
    infant_feeding_option:       "",
    /* Counselling checklist */
    pretest_counselling_done:    false,
    posttest_counselling_done:   false,
    adherence_counselling_done:  false,
    iycf_counselling_done:       false,
    referred_psychosocial:       false,
};

/* ─── Lab result helpers (from formData) ─── */
function deriveIsHivPositive(formData) {
    const val = (formData.hiv_test_result || "").toLowerCase();
    return val === "reactive" || val === "positive";
}
function latestNumericResult(val) {
    const n = Number(val);
    return isNaN(n) || val === "" ? null : n;
}
function latestResult(val) {
    return (val || "").toLowerCase();
}

/**
 * useEMTCT — manages eMTCT data.
 * Lab results are fetched read-only from the lab module.
 * Clinical decisions are upserted via POST /obgyn/emtct.
 */
export const useEMTCT = function(actions, patient_number, visit_id) {

    const _bookingData = useState(null);
    const bookingData = _bookingData[0];
    const setBookingData = _bookingData[1];

    const _loadingBooking = useState(true);
    const loadingBooking = _loadingBooking[0];
    const setLoadingBooking = _loadingBooking[1];

    const _emtctRecord = useState(null);
    const emtctRecord = _emtctRecord[0];
    const setEmtctRecord = _emtctRecord[1];

    const _loadingEmtct = useState(false);
    const loadingEmtct = _loadingEmtct[0];
    const setLoadingEmtct = _loadingEmtct[1];

    const _formData = useState(Object.assign({}, INITIAL_FORM));
    const formData = _formData[0];
    const setFormData = _formData[1];

    const _saving = useState(false);
    const saving = _saving[0];
    const setSaving = _saving[1];

    const _activeStep = useState(0);
    const activeStep = _activeStep[0];
    const setActiveStep = _activeStep[1];

    const _activeView = useState("form");
    const activeView = _activeView[0];
    const setActiveView = _activeView[1];

    const _emtctHistory = useState([]);
    const emtctHistory = _emtctHistory[0];
    const setEmtctHistory = _emtctHistory[1];

    const _loadingHistory = useState(false);
    const loadingHistory = _loadingHistory[0];
    const setLoadingHistory = _loadingHistory[1];

    const TOTAL_STEPS = 3;

    /* ═══ Fetch history (all eMTCT records for this patient) ═══ */
    const fetchHistory = useCallback(function() {
        if (!patient_number) return;
        setLoadingHistory(true);
        axios
            .get(config.smsUrl + "/obgyn/emtct/patient/" + patient_number)
            .then(function(res) {
                setEmtctHistory(Array.isArray(res.data) ? res.data : []);
            })
            .catch(function() { setEmtctHistory([]); })
            .finally(function() { setLoadingHistory(false); });
    }, [patient_number]);

    useEffect(function() {
        fetchHistory();
    }, [fetchHistory]);

    /* ═══ View toggle ═══ */
    const toggleView = useCallback(function(view) {
        setActiveView(view);
    }, []);

    /* ═══ Fetch ANC booking ═══ */
    useEffect(function() {
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/patient/" + patient_number)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) setBookingData(data);
            })
            .catch(function() { setBookingData(null); })
            .finally(function() { setLoadingBooking(false); });
    }, [patient_number]);

    /* ═══ Form handlers ═══ */
    const handleChange = useCallback(function(field, value) {
        setFormData(function(prev) {
            const obj = {};
            obj[field] = value;
            return Object.assign({}, prev, obj);
        });
    }, []);

    const toggleBool = useCallback(function(field) {
        setFormData(function(prev) {
            const obj = {};
            obj[field] = !prev[field];
            return Object.assign({}, prev, obj);
        });
    }, []);

    /* ═══ Step navigation ═══ */
    const handleNext = useCallback(function() {
        setActiveStep(function(s) { return Math.min(s + 1, TOTAL_STEPS - 1); });
    }, []);

    const handleBack = useCallback(function() {
        setActiveStep(function(s) { return Math.max(s - 1, 0); });
    }, []);

    const handleStepClick = useCallback(function(index) {
        setActiveStep(index);
    }, []);

    /* ═══ Save (POST — backend upserts by booking_id) ═══ */
    const saveEmtct = useCallback(function() {
        if (!bookingData) return;
        setSaving(true);
        const payload = Object.assign({}, formData, {
            booking_id: bookingData.id,
            seen_by:    visit_id,
        });
        axios
            .post(config.smsUrl + "/obgyn/emtct", payload)
            .then(function(res) {
                const data = res.data || payload;
                setEmtctRecord(data);
                actions.snackbarActions.snackSuccess("eMTCT record saved successfully");
                fetchHistory();
            })
            .catch(function(err) {
                errorMessages(err, null, actions);
            })
            .finally(function() { setSaving(false); });
    }, [bookingData, visit_id, formData, actions, fetchHistory]);

    /* ═══ Computed: Is HIV positive (from form data) ═══ */
    const isHivPositive = useMemo(function() {
        return deriveIsHivPositive(formData);
    }, [formData]);

    /* ═══ Computed: Alerts ═══ */
    const alerts = useMemo(function() {
        const list = [];
        if (isHivPositive && !formData.woman_already_on_art) {
            list.push({ type: "critical", message: "HIV-positive mother NOT on ART — initiate immediately", icon: "🚨" });
        }
        const vl = latestNumericResult(formData.viral_load_count);
        if (vl !== null && vl > 1000) {
            list.push({ type: "critical", message: "Unsuppressed viral load (" + vl + " copies/ml) — needs enhanced adherence", icon: "⚠" });
        }
        if (isHivPositive && !formData.partner_tested) {
            list.push({ type: "warning", message: "Partner not yet tested for HIV — provide STK or refer", icon: "👤" });
        }
        const syphResult = latestResult(formData.syphilis_result);
        if (syphResult === "reactive" && !formData.syphilis_woman_treated) {
            list.push({ type: "warning", message: "Syphilis reactive — treatment needed for mother", icon: "🧫" });
        }
        const hepResult = latestResult(formData.hepatitis_b_result);
        if (hepResult === "positive" || hepResult === "reactive") {
            list.push({ type: "warning", message: "Hepatitis B positive — plan infant vaccination at birth", icon: "🦠" });
        }
        const cd4 = latestNumericResult(formData.cd4_count);
        if (cd4 !== null && cd4 < 200) {
            list.push({ type: "critical", message: "CD4 count <200 — advanced HIV disease, requires urgent intervention", icon: "⚠" });
        }
        return list;
    }, [formData, isHivPositive]);

    /* ═══ Computed: Testing step (from lab results) ═══ */
    const testingStep = useMemo(function() {
        const val = (formData.hiv_test_result || "").toLowerCase();
        if (val === "non_reactive" || val === "negative") return "negative";
        if (val === "reactive" || val === "positive") return "confirmed_positive";
        return "initial";
    }, [formData]);

    /* ═══ Computed: GA ═══ */
    const gestationalAge = useMemo(function() {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
    }, [bookingData]);

    /* ═══ Section completion ═══ */
    const sectionStatus = useMemo(function() {
        const artDone     = !isHivPositive || !!formData.woman_art_regimen;
        const partnerDone = !!formData.partner_tested;
        const syphDone    = !!formData.syphilis_result;
        const hepDone     = !!formData.hepatitis_b_result;
        const infantDone  = !isHivPositive || !!formData.infant_feeding_option;
        const counselDone = formData.pretest_counselling_done || formData.posttest_counselling_done;
        return {
            lab_results:  !!formData.hiv_test_result,
            art_tracking: artDone,
            partner:      partnerDone,
            syphilis:     syphDone,
            hepatitis:    hepDone,
            infant:       infantDone,
            counselling:  !!counselDone,
        };
    }, [formData, isHivPositive]);

    return {
        bookingData:       bookingData,
        loadingBooking:    loadingBooking,
        loadingEmtct:      loadingEmtct,
        emtctRecord:       emtctRecord,
        formData:          formData,
        handleChange:      handleChange,
        toggleBool:        toggleBool,
        saveEmtct:         saveEmtct,
        saving:            saving,
        alerts:            alerts,
        testingStep:       testingStep,
        gestationalAge:    gestationalAge,
        isHivPositive:     isHivPositive,
        sectionStatus:     sectionStatus,
        activeStep:        activeStep,
        handleNext:        handleNext,
        handleBack:        handleBack,
        handleStepClick:   handleStepClick,
        activeView:        activeView,
        toggleView:        toggleView,
        emtctHistory:      emtctHistory,
        loadingHistory:    loadingHistory,
        /* Constants */
        WHO_STAGES:             WHO_STAGES,
        ART_REGIMENS:           ART_REGIMENS,
        FEEDING_OPTIONS:        FEEDING_OPTIONS,
        PARTNER_RESULT_OPTIONS: PARTNER_RESULT_OPTIONS,
    };
};
